/**
 * JAI v2 Design System
 *
 * Shared primitives for the v2 interface (June 2026 redesign, restyled Aug 2026
 * to read as a sports product instead of a generic AI dashboard).
 * Every page converges on this look: navy sidebar, #FAFBFD content base,
 * white hairline-bordered cards, mono stat cells.
 *
 * Tokens and component specs come from the design handoff
 * ("JAI Redesign v2") — values are intentional, match them closely.
 *
 * V2Card, V2.gradient and V2.darkHero are kept working as-is for the 8 tool
 * pages not yet migrated off them. New work should use the tiered card
 * primitives (V2List/V2ListRow/V2Section/V2Emphasis) and V2Dial icons below.
 */

import CreditCost from './CreditCost'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import { JAIMascot } from './JAIMascot'
import { STAT_GLYPHS } from '../config/statGlyphs'
import { timeAgo } from '../utils/datetime'
import { IS_NATIVE_APP } from '../services/runtime'

// ── Tokens ────────────────────────────────────────────────────────────────────

export const V2 = {
  ...JAI_COLORS,
  base: '#FAFBFD',
  navyInk: '#071533',
  warning: '#9A6A00',
  successOnDark: '#4ADE94',
  errorOnDark: '#FF6B6B',
  faint: 'rgba(10,31,68,0.40)',
  hairSoft: 'rgba(10,31,68,0.08)',
  // Flat, not gradients — kept as solid colors (rather than deleted) so the
  // pages still reading these two tokens don't lose their button/hero fills.
  gradient: '#1E5BD6',
  darkHero: '#0A1F44',
  // The one gradient the audit allows to survive in the whole app — reserved
  // for the LeagueHome win-probability bar. Don't reach for this anywhere else.
  winProbGradient: 'linear-gradient(90deg, #1E5BD6, #3A7CFF)',
  cardShadow: '0 1px 6px rgba(10,31,68,0.04)',
  composerShadow: '0 2px 8px rgba(10,31,68,0.05), 0 12px 40px rgba(10,31,68,0.07)',
  ctaShadow: '0 3px 10px rgba(58,124,255,0.28)',
  emphasisShadow: '0 4px 20px rgba(30,91,214,0.08)',
}

// ── Semantic color system ──────────────────────────────────────────────────
// Color that carries meaning instead of decoration — modeled on the heat()
// function in ScheduleSurgeon.jsx, the one place in the app that already
// does this well. Reach for these instead of blue-at-N-opacity.

export const V2Semantic = {
  hot:      '#1A8754',                // trending up / good form / win
  cold:     '#D63A3A',                // trending down / bad form / loss
  you:      '#1E5BD6',                // your team, your player
  opponent: 'rgba(10,31,68,0.38)',    // structural gray — recedes vs. "you"
  neutral:  'rgba(10,31,68,0.38)',    // structural gray, chrome-level
}

// Position colors — distinct hues so a position reads before the label does.
// NBA G/F (flex guard/forward slots) added for the Draft Board rebuild —
// verified live against espnService.js's ESPN_POSITION_MAPS (positionId 6/7)
// and Sleeper's players/nba dump before adding: both really do return bare
// 'G'/'F', not a hyphenated or slash combo like 'G-F' — so no combo keys
// were added, since nothing observed live actually produces one.
export const POSITION_COLORS = {
  PG: '#1E5BD6', SG: '#3A7CFF', SF: '#1A8754', PF: '#B8860B', C: '#8B4FD1',
  G: '#1E5BD6', F: '#1A8754',
  QB: '#1E5BD6', RB: '#1A8754', WR: '#3A7CFF', TE: '#B8860B', K: '#8B4FD1',
  'D/ST': 'rgba(10,31,68,0.45)',
}

export function posColor(pos) {
  return POSITION_COLORS[String(pos || '').toUpperCase()] || V2.blue
}

// ── Solid-fill contrast helper ─────────────────────────────────────────────
// Replaces the low-opacity-wash-plus-colored-text pattern (chips, badges,
// grade cells) with a full-strength fill + a contrast-safe ink, computed by
// WCAG relative luminance rather than a hand-maintained per-color list —
// same reasoning as the precomputed `ink` field in teamColors.js, just done
// at runtime since these colors aren't a fixed per-team table.

function parseColorToRgb(color) {
  if (color.startsWith('#')) {
    const h = color.slice(1)
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
    const num = parseInt(full, 16)
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 }
  }
  const m = color.match(/rgba?\(([^)]+)\)/)
  if (m) {
    const [r, g, b] = m[1].split(',').map(s => parseFloat(s))
    return { r, g, b }
  }
  return { r: 10, g: 31, b: 68 }
}

function relativeLuminance({ r, g, b }) {
  const [R, G, B] = [r, g, b].map(v => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * R + 0.7152 * G + 0.0722 * B
}

function contrastRatio(l1, l2) {
  const [lighter, darker] = l1 > l2 ? [l1, l2] : [l2, l1]
  return (lighter + 0.05) / (darker + 0.05)
}

// Returns { background, color } — a full-strength (fully opaque) fill with
// white text, or V2.navy text if the fill is too light for white to hit
// 4.5:1 contrast. Normalizes rgba() inputs to solid rgb() so a translucent
// token (e.g. V2Semantic.neutral) can't leak through as a wash.
export function solidChip(color) {
  const { r, g, b } = parseColorToRgb(color)
  const l = relativeLuminance({ r, g, b })
  const whiteOk = contrastRatio(l, 1) >= 4.5
  const background = color.startsWith('#') ? color : `rgb(${r}, ${g}, ${b})`
  return { background, color: whiteOk ? '#FFFFFF' : V2.navy }
}

// ── Percentage color banding ────────────────────────────────────────────────
// Fixed three-band scale for every percentage metric in the app: >=80 green,
// 50-79 orange, <=49 red. `invert: true` flips it for metrics where high is
// bad (risk %, "riskier than N% of the league"). Accepts either a 0-1
// fraction or a 0-100 number — normalizes internally so call sites don't
// each need their own *100.

export function pctTone(value, { invert = false } = {}) {
  if (value == null || Number.isNaN(value)) return V2.faint
  let pct = value <= 1 ? value * 100 : value
  if (invert) pct = 100 - pct
  if (pct >= 80) return V2Semantic.hot
  if (pct >= 50) return V2.warning
  return V2Semantic.cold
}

// ── Shared stroke icons (1.4px, currentColor) ─────────────────────────────────

export const V2Icons = {
  arrowUp: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  plus: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  league: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 1.7v12.6M1.7 8h12.6M3.2 3.2l9.6 9.6M12.8 3.2 3.2 12.8" stroke="currentColor" strokeWidth="1.1" opacity="0.5" />
    </svg>
  ),
  globe: (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M9 2c0 0-3 3-3 7s3 7 3 7M9 2c0 0 3 3 3 7s-3 7-3 7M2 9h14" stroke="currentColor" strokeWidth="1.2" opacity="0.65" />
    </svg>
  ),
  check: (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
    </svg>
  ),
  chevronDown: (
    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
      <path d="M2 3.5 5 6.5 8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  refresh: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M13.5 8a5.5 5.5 0 1 1-1.61-3.89" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M13.5 1.5v3h-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  waiver: (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6 9l2 2 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  trade: (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
      <path d="M3 11h12M12 8l3 3-3 3M15 7H3M6 4 3 7l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  draft: (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="2" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 6h8M5 9h6M5 12h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
}

// ── V2Dial — tool icon frame built from JaiMark's arc/dial geometry ───────────
// Replaces the tinted-square-plus-generic-glyph pattern (used 29x pre-redesign).
// Same circle + 108° arc as the JAI logo mark, so the icon set reads as JAI's
// own rather than a generic icon library. No tint-chip container.

export function V2Dial({ children, size = 40, accent = V2.blue, ink = V2.navy }) {
  const r = 14
  const cx = 18, cy = 18
  const arcAngle = 108
  const rad = (arcAngle * Math.PI) / 180
  const endX = cx + r * Math.sin(rad)
  const endY = cy - r * Math.cos(rad)
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none" style={{ color: ink }}>
      <circle cx={cx} cy={cy} r={r} stroke={accent} strokeWidth="1.3" opacity="0.16" />
      <path d={`M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${endX} ${endY}`} stroke={accent} strokeWidth="1.6" strokeLinecap="round" />
      <g stroke={ink} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
        {children}
      </g>
    </svg>
  )
}

// Glyphs sized to sit inside V2Dial's r=14 bezel (viewBox 0 0 36 36, center 18,18).
export const V2ToolGlyphs = {
  waiver: (
    <path d="M18 23.5V13M13.5 17l4.5-4.5 4.5 4.5" />
  ),
  trade: (
    <>
      <path d="M12 15.5h8.5M17.5 12.5l3 3-3 3" />
      <path d="M24 20.5h-8.5M18.5 23.5l-3-3 3-3" />
    </>
  ),
  // Trade Analyser sub-sections (Analyse Your Trade / Trade Radar) — 'trade'
  // above stays the tool-level glyph (ChatPage, LeagueHome tool grid).
  tradeAnalyse: (
    <>
      <path d="M12 15.5h8.5M17.5 12.5l3 3-3 3" />
      <path d="M24 20.5h-8.5M18.5 23.5l-3-3 3-3" />
      <path d="M15 20.5l1.6 1.6L20 18.5" opacity="0.55" />
    </>
  ),
  tradeRadar: (
    <>
      <circle cx="18" cy="18" r="2.3" />
      <circle cx="18" cy="18" r="6" opacity="0.55" />
      <circle cx="18" cy="18" r="9.5" opacity="0.28" />
      <path d="M18 18l4.5-4.5" />
    </>
  ),
  player: (
    <>
      <circle cx="18" cy="14.5" r="2.6" />
      <path d="M12.5 24c0-3.5 2.5-6 5.5-6s5.5 2.5 5.5 6" />
    </>
  ),
  team: (
    <path d="M13 23v-4M18 23v-7M23 23v-10" />
  ),
  scout: (
    <>
      <circle cx="18" cy="18" r="5" />
      <path d="M18 10v3M18 23v3M10 18h3M23 18h3" />
    </>
  ),
  draft: (
    <>
      <rect x="12.5" y="12.5" width="11" height="11" rx="1.5" />
      <path d="M12.5 17h11M17.5 12.5v11" />
    </>
  ),
  // Draft Board sub-sections (Strategy Establishment / Draft Review) — 'live'
  // above already covers Live Draft.
  strategy: (
    <>
      <circle cx="18" cy="18" r="6.5" />
      <circle cx="18" cy="18" r="2.4" />
    </>
  ),
  review: (
    <>
      <rect x="12.5" y="12" width="11" height="12" rx="1.5" />
      <path d="M15 17.5l2 2 4-4.5" />
    </>
  ),
  schedule: (
    <>
      <rect x="12" y="13" width="12" height="10" rx="1.5" />
      <path d="M15 11v3M21 11v3M12 16.5h12" />
    </>
  ),
  insurance: (
    <>
      <path d="M18 11l5.5 2.2v4c0 4-2.3 6.8-5.5 7.8-3.2-1-5.5-3.8-5.5-7.8v-4L18 11Z" />
      <path d="M15.3 18l2 2 3.4-3.6" />
    </>
  ),
  notes: (
    <>
      <path d="M13.5 12h6.5l3 3v9h-9.5V12Z" />
      <path d="M20 12v3h3M15.5 17.5h5M15.5 20h5" />
    </>
  ),
  // Secondary-row glyphs (TOOL_PILLS) — same dial language, smaller footprint.
  live: (
    <>
      <circle cx="18" cy="18" r="2.3" />
      <circle cx="18" cy="18" r="6" opacity="0.55" />
      <circle cx="18" cy="18" r="9.5" opacity="0.28" />
    </>
  ),
  news: (
    <>
      <rect x="11.5" y="12" width="13" height="12" rx="1" />
      <path d="M14 16h7M14 19h7M14 22h4" />
    </>
  ),
  trophy: (
    <>
      <path d="M18 22v2.5M15.5 24.5h5" />
      <path d="M13.5 12h9v4a4.5 4.5 0 0 1-9 0v-4Z" />
      <path d="M13.5 13.3H11v2a2.2 2.2 0 0 0 1.5 2.1M22.5 13.3H25v2a2.2 2.2 0 0 1-1.5 2.1" />
    </>
  ),
  bench: (
    <>
      <path d="M11.5 23.5h13M14 23.5v-4.5M22 23.5v-4.5M12.5 19h11" />
      <path d="M15.5 16l2.5-2.5 2.5 2.5" opacity="0.6" />
    </>
  ),
  dna: (
    <>
      <path d="M14.5 12c0 4 7 4 7 6s-7 2-7 6" />
      <path d="M21.5 12c0 4-7 4-7 6s7 2 7 6" />
      <path d="M15 16.3h6M15 19.7h6" opacity="0.6" />
    </>
  ),
  grudge: (
    <path d="M18 12l1.9 4 4.4.5-3.3 3 .9 4.3-3.9-2.2-3.9 2.2.9-4.3-3.3-3 4.4-.5 1.9-4Z" />
  ),
  power: (
    <>
      <path d="M12 22l3.5-5 2.5 2.5 4-6.5" />
      <path d="M12 22h12" opacity="0.5" />
    </>
  ),
  trash: (
    <>
      <path d="M13.5 14.5h9M15 14.5l.7 8.5h4.6l.7-8.5" />
      <path d="M16 14.5v-1.7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.7" opacity="0.6" />
    </>
  ),
  crystal: (
    <>
      <circle cx="18" cy="17" r="6" />
      <circle cx="18" cy="17" r="2.6" opacity="0.5" />
      <path d="M12.5 24.5h11" opacity="0.6" />
    </>
  ),
  globe: (
    <>
      <circle cx="18" cy="18" r="7" />
      <path d="M18 11c0 0-3.5 3-3.5 7s3.5 7 3.5 7M18 11c0 0 3.5 3 3.5 7s-3.5 7-3.5 7M11 18h14" opacity="0.6" />
    </>
  ),
}

// ── Page scaffold ──────────────────────────────────────────────────────────────
// Renders inside <AppShell>: centered container + kicker/title/sub header
// with an optional right-aligned action (StatStrip, link, button).

export function V2Page({ kicker, title, sub, action, maxWidth = 880, topBar, children }) {
  // kicker accepts a plain node (back-compat) OR an array of parts for
  // live state — week, lock time, sync status, deadline — dot-joined like
  // LeagueHome's header. Falsy parts are dropped so callers can inline
  // conditionals without cluttering the array.
  const kickerParts = Array.isArray(kicker) ? kicker.filter(Boolean) : null

  return (
    <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
      <div style={{ maxWidth, margin: '0 auto' }}>
        {topBar}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18, marginBottom: 22, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            {kicker && (
              <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: V2.faint, fontFamily: JAI_FONTS.mono, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {kickerParts
                  ? kickerParts.map((part, i) => (
                      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {i > 0 && <span style={{ opacity: 0.5 }}>·</span>}
                        {part}
                      </span>
                    ))
                  : kicker}
              </div>
            )}
            <h1 style={{ fontSize: 30, fontWeight: 800, color: V2.navy, letterSpacing: '-0.035em', margin: 0, lineHeight: 1 }}>
              {title}
            </h1>
            {sub && <p style={{ fontSize: 14, color: V2.mute, margin: '7px 0 0' }}>{sub}</p>}
          </div>
          {action}
        </div>
        {children}
      </div>
    </div>
  )
}

// ── Cards & sections ──────────────────────────────────────────────────────────

export function V2Card({ children, pad = '18px 20px', style = {} }) {
  return (
    <div style={{ background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 16, padding: pad, boxShadow: V2.cardShadow, ...style }}>
      {children}
    </div>
  )
}

export function V2SectionHead({ children, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: V2.navy, letterSpacing: '0.11em', textTransform: 'uppercase', flexShrink: 0 }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: 'rgba(10,31,68,0.09)' }} />
      {right && <span style={{ fontSize: 12.5, fontWeight: 600, color: V2.blue, flexShrink: 0 }}>{right}</span>}
    </div>
  )
}

// ── Card tiers ─────────────────────────────────────────────────────────────
// V2Card (above) is a single "floating card" look reused everywhere — the
// #1 tell in the redesign audit. These three tiers replace it for new work:
// list row (dense, repeating data), section (grouped content, ruled not
// boxed), emphasis (the one loud thing on the screen). V2Card itself is
// left alone — 8 tool pages still depend on its exact look.

// List row — flush, top/bottom hairlines only, no radius, no shadow. Wrap
// a run of V2ListRow in V2List for players, streamers, notes, waiver targets —
// anything that repeats and shouldn't compete for attention row-by-row.
export function V2List({ children, style = {} }) {
  return (
    <div style={{ borderTop: `1px solid ${V2.hairSoft}`, ...style }}>
      {children}
    </div>
  )
}

export function V2ListRow({ as: Tag = 'div', children, pad = '13px 18px', gap = 14, style = {}, ...rest }) {
  return (
    <Tag
      {...rest}
      style={{
        display: 'flex', alignItems: 'center', gap, padding: pad,
        borderBottom: `1px solid ${V2.hairSoft}`,
        textDecoration: 'none', color: 'inherit', ...style,
      }}
    >
      {children}
    </Tag>
  )
}

// Section — hairline-ruled group with a heading. Thin wrapper over
// V2SectionHead so grouped content (a tile grid, a list) doesn't need its
// own floating-card frame.
export function V2Section({ heading, right, children, style = {} }) {
  return (
    <div style={{ ...style }}>
      {heading && <V2SectionHead right={right}>{heading}</V2SectionHead>}
      {children}
    </div>
  )
}

// Emphasis block — solid navy, flat (no gradient). Reserved for the single
// most important thing on a screen — max one per page. Polymorphic via `as`
// so a page can render it as a Link without JaiV2 depending on the router.
export function V2Emphasis({ as: Tag = 'div', children, pad = '20px 24px', style = {}, ...rest }) {
  return (
    <Tag
      {...rest}
      style={{
        background: V2.navy, borderRadius: 16, padding: pad, color: 'white',
        display: 'block', textDecoration: 'none', ...style,
      }}
    >
      {children}
    </Tag>
  )
}

// ── StatStrip — row of mono stat cells ─────────────────────────────────────────
// cells: array of [value, label, accent?]
// accent: true (legacy "highlight this one blue"), a semantic color string
// (e.g. V2Semantic.hot for a win streak), or omitted for a plain cell.
// Big mono numbers next to tiny small-caps labels — sports interfaces are
// typographic; this is the highest-leverage, lowest-cost place to show it.

export function StatStrip({ cells, dark = false }) {
  return (
    <div style={{ display: 'flex', gap: 1, borderRadius: 10, overflow: 'hidden', border: dark ? '1px solid rgba(255,255,255,0.1)' : `1px solid ${V2.hairSoft}`, flexShrink: 0 }}>
      {cells.map(([value, label, accent], i) => {
        const color = accent === true ? (dark ? V2.blueBright : V2.blue) : (accent || null)
        const chip = color ? solidChip(color) : null
        return (
          <div
            key={i}
            style={{
              background: chip ? chip.background : (dark ? 'rgba(255,255,255,0.04)' : 'white'),
              padding: IS_NATIVE_APP ? '6px 10px' : '9px 16px', textAlign: 'center',
            }}
          >
            {/* Shrunk on native — five of these across a 390-430pt screen
                at the desktop size ran the strip taller than it needed to
                be and pushed everything else below the fold. */}
            <div style={{ fontSize: IS_NATIVE_APP ? 18 : 27, fontWeight: 800, color: chip ? chip.color : (dark ? 'white' : V2.navy), fontFamily: JAI_FONTS.mono, letterSpacing: '-0.03em', lineHeight: 1 }}>
              {value}
            </div>
            <div style={{ fontSize: IS_NATIVE_APP ? 7.5 : 9, letterSpacing: '0.13em', color: chip ? chip.color : (dark ? 'rgba(255,255,255,0.38)' : V2.faint), opacity: chip ? 0.85 : 1, fontFamily: JAI_FONTS.mono, marginTop: IS_NATIVE_APP ? 3 : 5, textTransform: 'uppercase' }}>
              {label}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Pills & badges ─────────────────────────────────────────────────────────────

const STATUS_MAP = {
  ACTIVE:       { c: V2.success, label: 'Active' },
  OUT:          { c: V2.error,   label: 'Out' },
  IR:           { c: V2.error,   label: 'IR' },
  GTD:          { c: V2.warning, label: 'GTD' },
  QUESTIONABLE: { c: V2.warning, label: 'GTD' },
}

// Statuses the platforms spell their own way. The backend normalises ESPN's
// (utils/injuryStatus.js); this covers older cached data and Yahoo/Sleeper
// spellings so a pill never shows raw "DAY_TO_DAY" or calls an unknown status healthy.
const STATUS_ALIASES = {
  DAY_TO_DAY: 'GTD', DTD: 'GTD', DOUBTFUL: 'GTD', PROBABLE: 'GTD',
  INJURY_RESERVE: 'IR', INJ: 'IR', O: 'OUT', SUSPENSION: 'OUT', SUSPENDED: 'OUT', SUS: 'OUT',
}

/** Display form of any platform status: { key, c, label } (never reads as healthy if it is not). */
export const statusDisplay = (status) => {
  const raw = String(status || 'ACTIVE').trim().toUpperCase().replace(/[\s-]+/g, '_')
  const key = STATUS_ALIASES[raw] || raw
  if (STATUS_MAP[key]) return { key, ...STATUS_MAP[key] }
  const label = raw.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')
  return { key, c: V2.warning, label }
}

export function StatusPill({ status }) {
  const s = statusDisplay(status)
  const injured = s.key !== 'ACTIVE'
  const chip = solidChip(s.c)
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999,
      background: chip.background, color: chip.color,
      fontFamily: JAI_FONTS.tight, whiteSpace: 'nowrap',
    }}>{injured && `${STAT_GLYPHS.injury} `}{s.label}</span>
  )
}

// ── Notice — the one way JAI shows a note, warning or error ───────────────────
// A thin solid rule marks the kind of note; the text stays dark and plain. No
// tinted fill, no outlined box (layout "C", chosen 13 Oct 2026). An optional
// action ("Try again") sits at the end as underlined text.
const NOTICE_TONE = { error: 'error', warning: 'warning', info: 'blue', success: 'success' }
export function Notice({ tone = 'info', label, children, action, onAction, style = {}, ...rest }) {
  const color = V2[NOTICE_TONE[tone]] || V2.blue
  return (
    <div
      role={tone === 'error' ? 'alert' : undefined}
      style={{ borderLeft: `3px solid ${color}`, paddingLeft: 12, borderRadius: 0, margin: '12px 0 16px', ...style }}
      {...rest}
    >
      {label && <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color, marginBottom: 3 }}>{label}</div>}
      <div style={{ fontSize: 13, lineHeight: 1.55, color: V2.navy, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ minWidth: 0 }}>{children}</div>
        {action && (
          <button onClick={onAction} style={{ color, textDecoration: 'underline', flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', padding: 0 }}>{action}</button>
        )}
      </div>
    </div>
  )
}

export function RiskBadge({ level }) {
  const map = { low: V2.success, medium: V2.warning, high: V2.error }
  const c = map[String(level).toLowerCase()] || V2.warning
  const chip = solidChip(c)
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
      background: chip.background, color: chip.color,
      letterSpacing: '0.04em', textTransform: 'uppercase',
      fontFamily: JAI_FONTS.tight, whiteSpace: 'nowrap',
    }}>{level} risk</span>
  )
}

export function PosChip({ children }) {
  if (!children) return null
  const chip = solidChip(posColor(children))
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: chip.color, background: chip.background, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  )
}

// ── JAI summary card — mascot-led takeaway ─────────────────────────────────────
// Built on V2Emphasis, not V2Card — a mascot-led takeaway IS "the one loud
// thing" V2Emphasis exists for, and every call site already uses it as the
// header/intro above a list (radar suggestions, lineup grade, etc.), so the
// bold navy treatment reads as an intentional lead-in rather than a mismatch,
// even on the 5 pages around it that are still on V2Card elsewhere.

export function JaiSummaryCard({ headline, children, thinking = false }) {
  return (
    <V2Emphasis pad="16px 20px" style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{ flexShrink: 0, marginTop: -2 }}>
        <JAIMascot size={42} state={thinking ? 'thinking' : 'idle'} animate={true} />
      </div>
      <div style={{ minWidth: 0 }}>
        {headline && <div style={{ fontSize: 15, fontWeight: 700, color: 'white', marginBottom: 4 }}>{headline}</div>}
        <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.8)', lineHeight: 1.65 }}>{children}</div>
      </div>
    </V2Emphasis>
  )
}

// ── JAI suggestion strip — dashed blue strip with mascot + action link ─────────

export function JaiSuggestionStrip({ children, actionLabel, onAction, style = {} }) {
  const chip = solidChip(V2.blue)
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', background: chip.background, borderRadius: 13, padding: '12px 16px', ...style }}>
      <JAIMascot size={30} state="idle" animate={false} />
      <span style={{ fontSize: 12.5, color: chip.color, fontWeight: 600, minWidth: 0 }}>{children}</span>
      {actionLabel && (
        <button
          onClick={onAction}
          style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: chip.color, flexShrink: 0, background: 'none', border: 'none', cursor: onAction ? 'pointer' : 'default', fontFamily: JAI_FONTS.tight, textDecoration: 'underline' }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  )
}

// ── Dark mini "JAI's plan/fix" card ────────────────────────────────────────────

export function DarkPlanCard({ title, children, linkLabel, onLink, style = {} }) {
  return (
    <div style={{ background: V2.darkHero, borderRadius: 16, padding: '16px 18px', color: 'white', ...style }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
        <JAIMascot size={32} state="idle" animate={false} />
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{title}</span>
      </div>
      <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.88)', lineHeight: 1.65 }}>{children}</div>
      {linkLabel && (
        <button
          onClick={onLink}
          style={{ fontSize: 11.5, fontWeight: 700, color: V2.blueBright, marginTop: 10, background: 'none', border: 'none', padding: 0, cursor: onLink ? 'pointer' : 'default', fontFamily: JAI_FONTS.tight }}
        >
          {linkLabel}
        </button>
      )}
    </div>
  )
}

// ── Solid blue tag ("JAI PICK" / "YOU" / "THIS WEEK") ──────────────────────────

export function BlueTag({ children }) {
  return (
    <span style={{ fontSize: 9.5, fontWeight: 800, color: 'white', background: V2.blue, borderRadius: 5, padding: '2px 8px', letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  )
}

// ── Progress / odds bar ────────────────────────────────────────────────────────
// tone: 'strong' (flat blue) · 'neutral' (translucent blue) · 'weak' (red)
// Flat, not gradient — the win-prob bar on LeagueHome is the one gradient
// the app keeps, and it doesn't run through V2Bar.

const BAR_FILL = {
  strong:  V2.blueBright,
  neutral: 'rgba(30,91,214,0.45)',
  weak:    '#D63A3A99',
}

export function V2Bar({ value, tone = 'strong', height = 7, track = 'rgba(10,31,68,0.06)', style = {} }) {
  return (
    <div style={{ height, borderRadius: 999, background: track, overflow: 'hidden', ...style }}>
      <div style={{ width: `${Math.max(0, Math.min(100, value))}%`, height: '100%', borderRadius: 999, background: BAR_FILL[tone] || tone }} />
    </div>
  )
}

// ── Table header row — mono uppercase labels over a CSS grid ───────────────────
// cols: array of label strings or [label, align] pairs

export function V2TableHead({ grid, cols, gap = 12 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: grid, gap, padding: '11px 20px', borderBottom: '1px solid rgba(10,31,68,0.07)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.12em', color: V2.faint, fontFamily: JAI_FONTS.mono, textTransform: 'uppercase' }}>
      {cols.map((c, i) => {
        const [label, align] = Array.isArray(c) ? c : [c, 'left']
        return <span key={i} style={{ textAlign: align }}>{label}</span>
      })}
    </div>
  )
}

// A button plus its "About N credits" estimate (config/creditCosts.js), side by
// side. `tool` is a CREDIT_COST key. Renders nothing extra for guests.
function withCreditCost(button, tool) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {button}
      <CreditCost tool={tool} />
    </span>
  )
}

// ── CTA buttons ────────────────────────────────────────────────────────────────

export function PrimaryBtn({ children, onClick, disabled, style = {}, credits }) {
  const button = (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: disabled ? 'rgba(10,31,68,0.12)' : V2.gradient,
        color: 'white', fontSize: 12.5, fontWeight: 700, borderRadius: 9,
        padding: '8px 16px', border: 'none',
        boxShadow: disabled ? 'none' : V2.ctaShadow,
        cursor: disabled ? 'default' : 'pointer', fontFamily: JAI_FONTS.tight,
        transition: 'filter 150ms', ...style,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter = 'brightness(0.92)' }}
      onMouseLeave={e => e.currentTarget.style.filter = 'none'}
    >
      {children}
    </button>
  )
  return credits ? withCreditCost(button, credits) : button
}

export function SecondaryBtn({ children, onClick, disabled, style = {} }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'white', color: V2.blue, fontSize: 12.5, fontWeight: 700,
        borderRadius: 9, padding: '8px 16px', border: '1px solid rgba(30,91,214,0.3)',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
        fontFamily: JAI_FONTS.tight, transition: 'background 150ms', ...style,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'rgba(30,91,214,0.05)' }}
      onMouseLeave={e => e.currentTarget.style.background = 'white'}
    >
      {children}
    </button>
  )
}

// ── Refresh button — filled pill, replaces bare "Refresh" text links ──────────
// loading: shows a spinning refresh glyph and disables the button.

export function RefreshButton({ onClick, loading = false, disabled = false, label = 'Refresh', loadingLabel = 'Refreshing…', style = {}, credits }) {
  const isDisabled = disabled || loading
  const button = (
    <button
      onClick={onClick}
      disabled={isDisabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: isDisabled ? 'rgba(10,31,68,0.15)' : V2.blueBright,
        color: 'white', fontSize: 12.5, fontWeight: 700, borderRadius: 999,
        padding: '7px 15px', border: 'none',
        cursor: isDisabled ? 'default' : 'pointer', fontFamily: JAI_FONTS.tight,
        transition: 'filter 150ms', flexShrink: 0, ...style,
      }}
      onMouseEnter={e => { if (!isDisabled) e.currentTarget.style.filter = 'brightness(0.92)' }}
      onMouseLeave={e => e.currentTarget.style.filter = 'none'}
    >
      <span style={{ display: 'inline-flex', animation: loading ? 'jaiSpin 0.8s linear infinite' : 'none' }}>
        {V2Icons.refresh}
      </span>
      {loading ? loadingLabel : label}
    </button>
  )
  return credits ? withCreditCost(button, credits) : button
}

// ── "Updated X ago" + refresh — the small status row under a page's hero/
// stat strip. One shell shared by every tool that shows a cached, refreshable
// result (Opponent Scout, Schedule Surgeon, Team Analyser) so "how fresh is
// this" always reads the same way. Deliberately quiet (11.5px, faint) — it
// must never compete with real content, like a live score.
export function UpdatedRow({ asOf, onRefresh, refreshing = false, label = 'Refresh now', style = {}, credits }) {
  const ago = timeAgo(asOf)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, ...style }}>
      <p style={{ textAlign: 'center', fontSize: 11.5, color: V2.faint, margin: 0 }}>
        {ago ? `Updated ${ago}` : 'Just now'}
      </p>
      <RefreshButton onClick={onRefresh} loading={refreshing} label={label} credits={credits} style={{ fontSize: 11.5, padding: '5px 12px' }} />
    </div>
  )
}

// ── Live score bar — a real scoreboard treatment, not a status line. Uses
// the SAME vocabulary and visual language as LeagueHome's ticker LIVE badge
// (pulsing green dot, uppercase mono "LIVE" tag) so "live" means the same
// thing everywhere in the app, and is never confusable with a "projected"
// number sitting next to it (see the projection StatStrip cell on Opponent
// Scout — this bar is deliberately a separate, heavier shape from that).
export function LiveScoreBar({ myTeamName = 'You', myPoints, theirTeamName = 'Them', theirPoints, style = {} }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
      padding: '12px 22px', borderRadius: 14, background: V2.navyInk, flexWrap: 'wrap', ...style,
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: V2.successOnDark, boxShadow: '0 0 8px rgba(74,222,148,0.8)' }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: V2.successOnDark, letterSpacing: '0.14em', textTransform: 'uppercase', fontFamily: JAI_FONTS.mono }}>
          Live
        </span>
      </span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
          {myTeamName}
        </span>
        <span style={{ fontSize: 24, fontWeight: 800, color: 'white', fontFamily: JAI_FONTS.mono, letterSpacing: '-0.02em' }}>{myPoints}</span>
      </div>
      {/* Dash travels WITH the second score block as one wrap unit — kept
          apart it orphans on line 1 when the bar wraps at narrow widths. */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, minWidth: 0 }}>
        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 15, flexShrink: 0 }}>–</span>
        <span style={{ fontSize: 24, fontWeight: 800, color: 'rgba(255,255,255,0.7)', fontFamily: JAI_FONTS.mono, letterSpacing: '-0.02em' }}>{theirPoints}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
          {theirTeamName}
        </span>
      </div>
    </div>
  )
}
